import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AUTH_TOKEN } from '../../../../constants'
import { GET_PREFILL_RECIPIENTS } from '../../../../graphql/query'
import PrefillExcelUploadModal from './PrefillExcelUploadModal'
import { PREFILL_MAX_IMPORT_ROWS } from './prefillFieldUtils'
import './PrefillDistributionPanel.scss'

export interface ExcelFormField {
  fieldId: number
  label: string
  type: string
  isEditField?: boolean
  parentFieldId?: number | null
  isTableCell?: boolean
  tableRowIndex?: number | null
  tableColIndex?: number | null
  prefillEditable?: boolean
  prefillEditMode?: string
  editableOnCondition?: Record<string, unknown> | null
  useAsRecipientName?: boolean
}

interface TemplateColumn {
  name: string
  required: boolean
  formRequired?: boolean
  fieldType: string
  fieldId?: number
  description: string
  options?: string[]
  isEditField?: boolean
  parentFieldId?: number | null
  isTableCell?: boolean
  tableRowIndex?: number | null
  tableColIndex?: number | null
  prefillEditable?: boolean
  prefillEditMode?: string
  editableOnCondition?: Record<string, unknown> | null
  useAsRecipientName?: boolean
}

interface Props {
  formUuid: string
}

const stepCircleStyle = (active: boolean, done: boolean): React.CSSProperties => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 16,
  backgroundColor: done ? '#198754' : active ? '#0d6efd' : '#e9ecef',
  color: done || active ? '#fff' : '#6c757d',
  flexShrink: 0
})

const getTemplateColumnRequiredLabel = (col: TemplateColumn): string => {
  if (col.required) return 'Yes'
  if (col.formRequired) return 'On form'
  return 'No'
}

const PrefillDistributionPanel: React.FC<Props> = ({ formUuid }) => {
  const navigate = useNavigate()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [templateColumns, setTemplateColumns] = useState<TemplateColumn[]>([])
  const [columnsLoading, setColumnsLoading] = useState(true)

  const excelFormFields = useMemo<ExcelFormField[]>(
    () =>
      templateColumns
        .filter((c) => c.fieldId != null)
        .map((c) => ({
          fieldId: c.fieldId!,
          label: c.name,
          type: c.fieldType,
          isEditField: c.isEditField,
          parentFieldId: c.parentFieldId,
          isTableCell: c.isTableCell,
          tableRowIndex: c.tableRowIndex,
          tableColIndex: c.tableColIndex,
          prefillEditable: c.prefillEditable,
          prefillEditMode: c.prefillEditMode,
          editableOnCondition: c.editableOnCondition,
          useAsRecipientName: c.useAsRecipientName === true
        })),
    [templateColumns]
  )

  const { data, refetch } = useQuery(GET_PREFILL_RECIPIENTS, {
    variables: { input: { formUuid, limit: PREFILL_MAX_IMPORT_ROWS, offset: 0 } },
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    skip: !formUuid
  })

  const recipientCount = data?.getPrefillRecipients?.total || 0
  const recipients = data?.getPrefillRecipients?.recipients || []
  const hasRecipients = recipientCount > 0

  const recipientStats = useMemo(
    () => ({
      pending: recipients.filter((r: { status: string }) => r.status === 'ready').length,
      sent: recipients.filter((r: { status: string }) =>
        ['sent', 'opened', 'revision_requested'].includes(r.status)
      ).length,
      submitted: recipients.filter((r: { status: string }) => r.status === 'submitted').length,
      totalMailsSent: recipients.reduce(
        (sum: number, r: { sentMailCount?: number }) => sum + (r.sentMailCount || 0),
        0
      )
    }),
    [recipients]
  )

  const recipientsPagePath = `/automation/process-automation/form-builder/share-form/${formUuid}/prefill-recipients`

  const goToRecipientsPage = () => navigate(recipientsPagePath)

  useEffect(() => {
    const loadColumns = async () => {
      if (!formUuid) return
      setColumnsLoading(true)
      try {
        const token = localStorage.getItem(AUTH_TOKEN)
        const res = await axios.get(
          `${process.env.REACT_APP_RESUME_PARSER_URL}/form-builder/${formUuid}/prefill-template-columns`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (res.data?.success && Array.isArray(res.data.columns)) {
          setTemplateColumns(res.data.columns)
        } else {
          setTemplateColumns([])
          toast.error('Failed to load Excel template columns')
        }
      } catch {
        setTemplateColumns([])
        toast.error('Failed to load Excel template columns')
      } finally {
        setColumnsLoading(false)
      }
    }
    loadColumns()
  }, [formUuid])

  const formFieldColumnCount = excelFormFields.length

  const handleDownloadTemplate = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem(AUTH_TOKEN)
      const res = await axios.get(
        `${process.env.REACT_APP_RESUME_PARSER_URL}/form-builder/${formUuid}/prefill-template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      )
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `prefill_template_${formUuid}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Excel template downloaded')
    } catch {
      toast.error('Failed to download template')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className='w-75'>
      <div className='card border border-black mb-4'>
        <div className='card-body'>
          <h3 className='fw-bold mb-1'>Share Prefilled Forms</h3>
          <p className='text-muted mb-0'>
            Follow the three steps below — each Excel row becomes a separate personalized form.
          </p>
        </div>
      </div>

      {/* Step 1 */}
      <div className='card border border-black mb-4'>
        <div className='card-body'>
          <div className='d-flex align-items-start gap-3'>
            <div style={stepCircleStyle(!hasRecipients, hasRecipients)}>1</div>
            <div className='flex-grow-1'>
              <h5 className='fw-bold mb-1'>Download Excel Template</h5>
              <p className='text-muted mb-3'>
                The columns below match the downloaded Excel template exactly — only your saved form
                input fields (short answer, checkbox, email, dropdown, date, etc.). Red column names
                in Excel mean required on the form (optional to fill in Excel). Only the email
                column is mandatory in Excel.
              </p>

              {formFieldColumnCount === 0 && !columnsLoading && (
                <div className='alert alert-warning'>
                  No input fields found on this form. Open the form builder, add fields using{' '}
                  <strong>Short answer</strong>, <strong>checkbox</strong>,{' '}
                  <strong>Email input</strong>, etc., then <strong>Save</strong> and return here.
                </div>
              )}

              <div className='table-responsive mb-3'>
                <table className='table table-sm table-bordered mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column name</th>
                      <th>Field type</th>
                      <th>Required</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columnsLoading ? (
                      <tr>
                        <td colSpan={4} className='text-muted'>
                          Loading columns from form...
                        </td>
                      </tr>
                    ) : (
                      templateColumns.map((col) => (
                        <tr key={col.name}>
                          <td>
                            <code
                              className={
                                col.formRequired || col.required
                                  ? 'prefill-template-column--form-required'
                                  : undefined
                              }
                            >
                              {col.name}
                              {col.required ? ' *' : ''}
                            </code>
                            {col.isEditField && (
                              <span className='badge bg-warning text-dark ms-1'>Edit-only</span>
                            )}
                            {col.isTableCell && (
                              <span className='badge bg-info text-dark ms-1'>Table cell</span>
                            )}
                          </td>
                          <td>{col.fieldType}</td>
                          <td>{getTemplateColumnRequiredLabel(col)}</td>
                          <td className='text-muted'>
                            {col.description}
                            {col.options && col.options.length > 0 && (
                              <div className='mt-1'>
                                <span className='badge bg-light text-dark border me-1'>
                                  Excel dropdown
                                </span>
                                <small>{col.options.join(' · ')}</small>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <button
                type='button'
                className='btn btn-primary'
                onClick={handleDownloadTemplate}
                disabled={downloading || columnsLoading}
              >
                <i className='bi bi-download me-2' />
                {downloading ? 'Downloading...' : 'Download Excel Template'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className='card border border-black mb-4'>
        <div className='card-body'>
          <div className='d-flex align-items-start gap-3'>
            <div style={stepCircleStyle(!hasRecipients, hasRecipients)}>2</div>
            <div className='flex-grow-1'>
              <h5 className='fw-bold mb-1'>Upload Filled Excel</h5>
              <p className='text-muted mb-3'>
                After filling the template with recipient data, upload the updated Excel file (up to{' '}
                {PREFILL_MAX_IMPORT_ROWS} rows, 15MB max). Every row must include a value in the
                required email column (e.g. <strong>Email ID</strong>) so links can be sent to
                recipients.
              </p>
              <button type='button' className='btn btn-primary' onClick={() => setUploadOpen(true)}>
                <i className='bi bi-upload me-2' />
                Upload Updated Excel
              </button>
              {hasRecipients && (
                <button
                  type='button'
                  className='btn btn-outline-secondary ms-2'
                  onClick={() => setUploadOpen(true)}
                >
                  Upload Another File
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3 */}
      <div className='card border border-black'>
        <div className='card-body'>
          <div className='d-flex align-items-start gap-3'>
            <div style={stepCircleStyle(hasRecipients, hasRecipients)}>3</div>
            <div className='flex-grow-1'>
              <h5 className='fw-bold mb-1'>Recipient Forms</h5>
              <p className='text-muted mb-3'>
                Each Excel row creates a separate form instance. View, preview, and manage all
                recipient forms on a dedicated page.
              </p>

              {hasRecipients ? (
                <div className='prefill-step-recipients'>
                  <div className='prefill-step-recipients__summary'>
                    <div className='prefill-step-recipients__summary-main'>
                      <p className='prefill-step-recipients__count'>{recipientCount}</p>
                      <p className='prefill-step-recipients__count-label'>
                        personalized form{recipientCount === 1 ? '' : 's'} ready to manage
                      </p>
                    </div>
                    <div className='prefill-step-recipients__metrics'>
                      <div className='prefill-step-recipients__metric'>
                        <span className='prefill-step-recipients__metric-value'>
                          {recipientStats.pending}
                        </span>
                        <span className='prefill-step-recipients__metric-label'>Pending</span>
                      </div>
                      <div className='prefill-step-recipients__metric'>
                        <span className='prefill-step-recipients__metric-value'>
                          {recipientStats.sent}
                        </span>
                        <span className='prefill-step-recipients__metric-label'>Sent</span>
                      </div>
                      <div className='prefill-step-recipients__metric'>
                        <span className='prefill-step-recipients__metric-value'>
                          {recipientStats.totalMailsSent}
                        </span>
                        <span className='prefill-step-recipients__metric-label'>Mails sent</span>
                      </div>
                      <div className='prefill-step-recipients__metric'>
                        <span className='prefill-step-recipients__metric-value'>
                          {recipientStats.submitted}
                        </span>
                        <span className='prefill-step-recipients__metric-label'>Submitted</span>
                      </div>
                    </div>
                  </div>

                  <div className='prefill-step-recipients__actions'>
                    <button type='button' className='btn btn-primary' onClick={goToRecipientsPage}>
                      <i className='bi bi-people me-2' />
                      Manage Recipient Forms
                    </button>
                    <button
                      type='button'
                      className='btn btn-outline-primary'
                      onClick={() => setUploadOpen(true)}
                    >
                      <i className='bi bi-upload me-2' />
                      Add More Recipients
                    </button>
                  </div>
                  <p className='text-muted small mt-3 mb-0' style={{ fontSize: '0.9375rem' }}>
                    Preview each form, send email links in bulk, track delivery status, and view
                    submissions — all from the recipient forms page.
                  </p>
                </div>
              ) : (
                <div className='prefill-step-recipients__empty'>
                  <i className='bi bi-person-lines-fill' />
                  <p className='fw-medium mb-1'>No recipient forms yet</p>
                  <p className='small'>
                    Complete <strong>Step 1</strong> and <strong>Step 2</strong> to create one
                    personalized form per Excel row.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PrefillExcelUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        formUuid={formUuid}
        excelFormFields={excelFormFields}
        onImported={() => {
          refetch()
          goToRecipientsPage()
        }}
      />
    </div>
  )
}

export default PrefillDistributionPanel
